/**
 * Performance Hub Page — 4-tab view: Appraisals, KRAs, Feedback, My Skills.
 *
 * Each tab fetches its own BLL endpoint independently.
 * Feedback tab has sub-tabs for Received/Given.
 */

import * as React from 'react';
import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@yesbos/ui';
import {
  Award,
  BarChart2,
  MessageSquare,
  Layers,
} from 'lucide-react';
import { hrosApi } from '@/lib/api';

// ── Types ────────────────────────────────────────────────────────────────────

interface Appraisal {
  id: string;
  title: string;
  appraisal_type: 'annual' | 'quarterly' | 'probation' | string;
  period_start: string;
  period_end: string;
  status: 'draft' | 'active' | 'review' | 'closed' | string;
}

interface KRA {
  id: string;
  name: string;
  description?: string;
  weightage: number;
  is_active: boolean;
  department_name?: string;
}

interface Feedback {
  id: string;
  feedback_type: string;
  rating: number;
  comments?: string;
  is_anonymous: boolean;
  from_name?: string;
  to_name?: string;
  created_at: string;
}

interface Skill {
  id: string;
  competency_name: string;
  category: string;
  self_rating: number;
  manager_rating?: number;
  notes?: string;
  assessed_on?: string;
}

interface BLLResponse {
  success: boolean;
  data?: unknown;
  error?: { message: string };
}

// ── BLL helper ────────────────────────────────────────────────────────────────

async function callBLL(path: string): Promise<BLLResponse> {
  return hrosApi.get(path) as Promise<BLLResponse>;
}

// ── Badge helpers ─────────────────────────────────────────────────────────────

function appraisalTypeBadgeClass(type: string): string {
  switch (type) {
    case 'annual':
      return 'bg-blue-100 text-blue-700 border-blue-200';
    case 'quarterly':
      return 'bg-green-100 text-green-700 border-green-200';
    case 'probation':
      return 'bg-amber-100 text-amber-700 border-amber-200';
    default:
      return 'bg-gray-100 text-gray-600 border-gray-200';
  }
}

function appraisalStatusBadgeClass(status: string): string {
  switch (status) {
    case 'active':
      return 'bg-green-100 text-green-700 border-green-200';
    case 'review':
      return 'bg-yellow-100 text-yellow-700 border-yellow-200';
    case 'draft':
    case 'closed':
      return 'bg-gray-100 text-gray-600 border-gray-200';
    default:
      return 'bg-gray-100 text-gray-600 border-gray-200';
  }
}

// ── Star Rating ───────────────────────────────────────────────────────────────

function StarRating({ value, max = 5 }: { value: number; max?: number }) {
  return (
    <span className="text-sm leading-none" aria-label={`${value} out of ${max} stars`}>
      {Array.from({ length: max }).map((_, i) => (
        <span
          key={i}
          className={i < Math.round(value) ? 'text-yellow-400' : 'text-muted-foreground/40'}
        >
          ★
        </span>
      ))}
    </span>
  );
}

// ── Rating Bar ────────────────────────────────────────────────────────────────

function RatingBar({
  label,
  value,
  max = 5,
  colorClass = 'bg-primary',
}: {
  label: string;
  value?: number;
  max?: number;
  colorClass?: string;
}) {
  const pct = value != null ? Math.round((value / max) * 100) : 0;
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="w-24 text-muted-foreground shrink-0">{label}</span>
      {value != null ? (
        <>
          <div className="flex-1 bg-muted rounded-full h-1.5 overflow-hidden">
            <div
              className={`h-full rounded-full ${colorClass}`}
              style={{ width: `${pct}%` }}
            />
          </div>
          <span className="w-6 text-right text-foreground font-medium">{value}</span>
        </>
      ) : (
        <span className="text-muted-foreground italic">Not rated</span>
      )}
    </div>
  );
}

// ── Loading Skeleton ──────────────────────────────────────────────────────────

function SkeletonGrid({ count = 6, height = 'h-28' }: { count?: number; height?: string }) {
  return (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className={`${height} rounded-lg border bg-muted animate-pulse`} />
      ))}
    </div>
  );
}

// ── Appraisals Tab ────────────────────────────────────────────────────────────

function AppraisalsTab() {
  const [appraisals, setAppraisals] = React.useState<Appraisal[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    callBLL('/v1/hros/performance/appraisals')
      .then((res) => {
        if (res.success && Array.isArray(res.data)) {
          setAppraisals(res.data as Appraisal[]);
        } else {
          setError(res.error?.message ?? 'Failed to load appraisals.');
        }
      })
      .catch(() => setError('Network error loading appraisals.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <SkeletonGrid />;

  if (error) {
    return <p className="text-sm text-destructive">{error}</p>;
  }

  if (appraisals.length === 0) {
    return (
      <EmptyState
        icon={<Award className="h-8 w-8" />}
        title="No Appraisal Cycles"
        description="No appraisal cycles have been configured yet."
      />
    );
  }

  return (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
      {appraisals.map((a) => (
        <Card key={a.id} className="hover:shadow-md transition-shadow">
          <CardHeader className="pb-2 pt-4 px-4">
            <CardTitle className="text-base font-medium">{a.title}</CardTitle>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <span
                className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs border ${appraisalTypeBadgeClass(a.appraisal_type)}`}
              >
                {a.appraisal_type}
              </span>
              <span
                className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs border ${appraisalStatusBadgeClass(a.status)}`}
              >
                {a.status}
              </span>
            </div>
          </CardHeader>
          <CardContent className="pb-4 px-4">
            <p className="text-xs text-muted-foreground">
              {new Date(a.period_start).toLocaleDateString('en-IN', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
              })}{' '}
              →{' '}
              {new Date(a.period_end).toLocaleDateString('en-IN', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
              })}
            </p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ── KRAs Tab ──────────────────────────────────────────────────────────────────

function KRAsTab() {
  const [kras, setKras] = React.useState<KRA[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    callBLL('/v1/hros/performance/kras')
      .then((res) => {
        if (res.success && Array.isArray(res.data)) {
          setKras(res.data as KRA[]);
        } else {
          setError(res.error?.message ?? 'Failed to load KRAs.');
        }
      })
      .catch(() => setError('Network error loading KRAs.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <SkeletonGrid />;

  if (error) {
    return <p className="text-sm text-destructive">{error}</p>;
  }

  if (kras.length === 0) {
    return (
      <EmptyState
        icon={<Layers className="h-8 w-8" />}
        title="No KRAs Defined"
        description="Key Result Areas have not been configured yet."
      />
    );
  }

  return (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
      {kras.map((kra) => (
        <Card key={kra.id} className="hover:shadow-md transition-shadow">
          <CardHeader className="pb-2 pt-4 px-4">
            <div className="flex items-start justify-between gap-2">
              <CardTitle className="text-base font-medium leading-snug">
                {kra.name}
              </CardTitle>
              <span
                className={`shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-xs border ${
                  kra.is_active
                    ? 'bg-green-100 text-green-700 border-green-200'
                    : 'bg-gray-100 text-gray-500 border-gray-200'
                }`}
              >
                {kra.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
          </CardHeader>
          <CardContent className="pb-4 px-4 space-y-2">
            {kra.description && (
              <p className="text-xs text-muted-foreground line-clamp-2">
                {kra.description}
              </p>
            )}
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">Weightage</span>
              <span className="text-sm font-semibold">{kra.weightage}%</span>
            </div>
            {kra.department_name && (
              <Badge variant="outline" className="text-xs">
                {kra.department_name}
              </Badge>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ── Feedback Card ─────────────────────────────────────────────────────────────

function FeedbackCard({ item }: { item: Feedback }) {
  const displayName = item.is_anonymous ? 'Anonymous' : (item.from_name ?? 'Unknown');
  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="pt-4">
        <div className="flex items-start justify-between gap-2 mb-2">
          <div>
            <p className="text-sm font-medium">{displayName}</p>
            <p className="text-xs text-muted-foreground">
              {new Date(item.created_at).toLocaleDateString('en-IN', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
              })}
            </p>
          </div>
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs border bg-blue-50 text-blue-700 border-blue-200 shrink-0">
            {item.feedback_type}
          </span>
        </div>
        <StarRating value={item.rating} />
        {item.comments && (
          <p className="text-xs text-muted-foreground mt-2 line-clamp-3">
            {item.comments}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

// ── Feedback Tab ──────────────────────────────────────────────────────────────

function FeedbackTab() {
  const [feedback, setFeedback] = React.useState<Feedback[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [subTab, setSubTab] = React.useState<'received' | 'given'>('received');

  React.useEffect(() => {
    setLoading(true);
    setError('');
    callBLL(`/v1/hros/performance/feedback?direction=${subTab}`)
      .then((res) => {
        if (res.success && Array.isArray(res.data)) {
          setFeedback(res.data as Feedback[]);
        } else {
          setError(res.error?.message ?? 'Failed to load feedback.');
        }
      })
      .catch(() => setError('Network error loading feedback.'))
      .finally(() => setLoading(false));
  }, [subTab]);

  return (
    <div className="space-y-4">
      {/* Sub-tabs */}
      <div className="flex gap-2 border-b pb-2">
        {(['received', 'given'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setSubTab(tab)}
            className={`px-3 py-1.5 text-sm rounded-md transition-colors capitalize ${
              subTab === tab
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {loading ? (
        <SkeletonGrid count={4} />
      ) : error ? (
        <p className="text-sm text-destructive">{error}</p>
      ) : feedback.length === 0 ? (
        <EmptyState
          icon={<MessageSquare className="h-8 w-8" />}
          title="No Feedback Yet"
          description={`You have not ${subTab} any feedback yet.`}
        />
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {feedback.map((item) => (
            <FeedbackCard key={item.id} item={item} />
          ))}
        </div>
      )}
    </div>
  );
}

// ── My Skills Tab ─────────────────────────────────────────────────────────────

function SkillsTab() {
  const [skills, setSkills] = React.useState<Skill[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    callBLL('/v1/hros/performance/skills')
      .then((res) => {
        if (res.success && Array.isArray(res.data)) {
          setSkills(res.data as Skill[]);
        } else {
          setError(res.error?.message ?? 'Failed to load skills.');
        }
      })
      .catch(() => setError('Network error loading skills.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="h-16 rounded-lg border bg-muted animate-pulse" />
        ))}
      </div>
    );
  }

  // Group by category — must be called unconditionally before any early return (Rules of Hooks)
  const grouped = React.useMemo(() => {
    const map = new Map<string, Skill[]>();
    for (const s of skills) {
      const cat = s.category ?? 'General';
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat)!.push(s);
    }
    return map;
  }, [skills]);

  if (error) return <p className="text-sm text-destructive">{error}</p>;

  if (skills.length === 0) {
    return (
      <EmptyState
        icon={<BarChart2 className="h-8 w-8" />}
        title="No Skills Assessed"
        description="Your competency assessments will appear here."
      />
    );
  }

  return (
    <div className="space-y-6">
      {Array.from(grouped.entries()).map(([category, items]) => (
        <div key={category}>
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
            {category}
          </h3>
          <div className="space-y-3">
            {items.map((skill) => (
              <Card key={skill.id}>
                <CardContent className="pt-3 pb-3">
                  <div className="flex items-start justify-between gap-4 mb-2">
                    <p className="text-sm font-medium">{skill.competency_name}</p>
                    {skill.assessed_on && (
                      <span className="text-xs text-muted-foreground shrink-0">
                        {new Date(skill.assessed_on).toLocaleDateString('en-IN', {
                          month: 'short',
                          year: 'numeric',
                        })}
                      </span>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    <RatingBar
                      label="Self Rating"
                      value={skill.self_rating}
                      colorClass="bg-blue-500"
                    />
                    <RatingBar
                      label="Manager Rating"
                      value={skill.manager_rating}
                      colorClass="bg-green-500"
                    />
                  </div>
                  {skill.notes && (
                    <p className="text-xs text-muted-foreground mt-2 line-clamp-2">
                      {skill.notes}
                    </p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export function PerformancePage() {
  const [activeTab, setActiveTab] = React.useState('appraisals');

  return (
    <div className="flex flex-col gap-6 p-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold">Performance</h1>
        <p className="text-sm text-muted-foreground mt-0.5">
          Appraisals, KRAs, feedback, and competency tracking.
        </p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="appraisals">Appraisals</TabsTrigger>
          <TabsTrigger value="kras">KRAs</TabsTrigger>
          <TabsTrigger value="feedback">Feedback</TabsTrigger>
          <TabsTrigger value="skills">My Skills</TabsTrigger>
        </TabsList>

        <TabsContent value="appraisals" className="mt-4">
          <AppraisalsTab />
        </TabsContent>

        <TabsContent value="kras" className="mt-4">
          <KRAsTab />
        </TabsContent>

        <TabsContent value="feedback" className="mt-4">
          <FeedbackTab />
        </TabsContent>

        <TabsContent value="skills" className="mt-4">
          <SkillsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default PerformancePage;
